// Libraries
import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";

// Components
import AdHelper from "../shared/ad/AdHelper";
import Careers from "../shared/careers/CareerTab";
import CategoryTags from "../news/CategoryTags";
import Loading from "../Loading";
import NewsCard from "../news/NewsCard";
import AdInRow from "../ad/AdInRow";
import FilterComponent from "../news/FilterComponent";

// Ant Design
import { Empty, message, BackTop, Button, Row, Col } from "antd";
import { PlusOutlined } from "@ant-design/icons";

// Helpers & Config
import NoCapManifest from "../../nocap.json";
import config from "../../config";
import axios from "axios";
import { capitalizeFirstLetterOfEachWord } from "../../helpers";

const NewsPage = (props) => {
  const [currentCategory, setCurrentCategory] = useState("");
  const [districts, setDistricts] = useState(NoCapManifest.candidateInfoForm.districts);
  const [inputState, setInputState] = useState({ state: "", division: "", district: "" });

  const [filteredData, setFilteredData] = useState([]);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const restoringFromPost = useRef(false); // 👈 Flag to track "go back" intent

  // Restore scroll/filter/category on first load
  useEffect(() => {
    const restoredCategory = location.state?.category;
    const restoredFilters = location.state?.filters;
    const restoredScroll = location.state?.scrollY;

    if (restoredCategory) {
      restoringFromPost.current = true;
      setCurrentCategory(restoredCategory);
    }

    const filtersAreValid = restoredFilters &&
      Object.values(restoredFilters).some((v) => v && v.trim() !== "");

    if (filtersAreValid) {
      setInputState(restoredFilters);
    }

    if (!restoredCategory && !filtersAreValid) {
      fetchData(); // no filters or category
    }

    if (restoredScroll !== undefined) {
      setTimeout(() => {
        window.scrollTo(0, restoredScroll);
      }, 200);
    }
  }, []);

  // Fetch or apply filters after category is set
  useEffect(() => {
    if (!currentCategory) return;

    if (restoringFromPost.current) {
      const filters = location.state?.filters;
      const filtersAreValid = filters &&
        Object.values(filters).some((v) => v && v.trim() !== "");

      if (filtersAreValid) {
        handleFilter(filters);
      } else {
        fetchData(); // just fetch based on category
      }

      restoringFromPost.current = false;
    } else {
      fetchData(); // regular category click
    }
  }, [currentCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios(`${config.BLOGS}/blogs/all`);
      const fetchedData = response.data;
      const dataFilteredByCategory = currentCategory
        ? fetchedData.filter(post => post.category === currentCategory)
        : fetchedData;

      setData(fetchedData);
      setFilteredData(dataFilteredByCategory);
    } catch (error) {
      setError(error);
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (filters) => {
    const { state, district } = filters;
    const shouldFilterState = state && state.trim() !== "";
    const shouldFilterDistrict = district && district.trim() !== "";

    const filtered = data.filter(item => {
      const stateMatch = shouldFilterState ? item.state?.includes(state) : true;
      const districtMatch = shouldFilterDistrict ? item.district?.includes(district) : true;
      const notArchived = !item.archived;
      const categoryMatch = currentCategory ? item.category === currentCategory : true;
      return stateMatch && districtMatch && notArchived && categoryMatch;
    });

    setFilteredData(filtered);
  };

  const handleCategoryChange = (c) => {
    setCurrentCategory(c);
    fetchData(); // force reload of this category
  };

  const handleFilterClear = () => {
    fetchData(); // just reload category-only data
  };

  const addDistrict = (e) => {
    e.preventDefault();
    const i = Date.now(); // better unique ID
    const addedDistrict = inputState.district
      ? capitalizeFirstLetterOfEachWord(inputState.district)
      : `New Area ${i}`;

    if (districts.includes(addedDistrict)) {
      message.error("District already exists");
      setInputState({ ...inputState, district: "" });
    } else {
      setDistricts([...districts, addedDistrict]);
      setInputState({ ...inputState, district: "" });
    }
  };

  if (error) return message.error("Oops ... Something went wrong loading data");

  const blogItems = filteredData.map((post, idx) => {
    if (post.archived) return null;
    return (
      <AdInRow count={idx + 1} key={post._id}>
        <Col xs={24} sm={12} md={8} lg={6} xl={6} style={{ padding: "0 1em", marginBottom: "2em" }}>
          <NewsCard
            post={post}
            pathname={`/news/${post._id}`}
            getData={fetchData}
            currentCategory={currentCategory}
            inputState={inputState}
          />
        </Col>
      </AdInRow>
    );
  });

  return (
    <div>
      <div className="news-ads-container">
        <AdHelper page="news" />
      </div>

      <br />

      <div className="news-cta-container">
        <CategoryTags
          categories={NoCapManifest.news.newsCategories}
          value={currentCategory}
          onChange={handleCategoryChange}
        />
      </div>

      <br />

      <FilterComponent
        onFilter={handleFilter}
        onClear={handleFilterClear}
      />

      <br />

      {loading ? (
        <Loading />
      ) : filteredData.length > 0 ? (
        <Row className="news-row">{blogItems}</Row>
      ) : currentCategory === "Careers" ? (
        <Careers careerPosts={[]} />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      <BackTop />

      <br />
      <div className="news-ads-container">
        <AdHelper page="news" />
      </div>
    </div>
  );
};

export default NewsPage;
